import { useEffect, useRef, ReactNode } from 'react';
import { Box, Paper, Stack, Typography, useTheme, SxProps, Theme } from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import { useDragDrop, type UseDragDropOptions } from '@/hooks/use-drag-drop';
import { useTranslation } from 'react-i18next';

interface DragDropZoneProps {
  children?: ReactNode;
  targetPath: string;
  onFilesDropped?: (files: File[], targetPath: string) => Promise<void>;
  onItemsDropped?: (itemIds: string[], targetPath: string) => Promise<void>;
  className?: string;
  sx?: SxProps<Theme>;
}

/**
 * Drag and Drop Zone Component
 * Provides visual feedback for drag-drop operations
 * Supports both file uploads and item movements
 */
export function DragDropZone({
  children,
  targetPath,
  onFilesDropped,
  onItemsDropped,
  className,
  sx,
}: DragDropZoneProps) {
  const theme = useTheme();
  const { t } = useTranslation('load');
  const zoneRef = useRef<HTMLDivElement>(null);

  const {
    dragState,
    attachDropZone,
  } = useDragDrop({
    onFilesDropped,
    onItemsDropped,
  });

  // Attach drop zone listeners
  useEffect(() => {
    if (!zoneRef.current) return;

    const cleanup = attachDropZone(zoneRef.current, targetPath);
    return cleanup;
  }, [targetPath, attachDropZone]);

  const isDragging = dragState.isDragging;

  return (
    <Box
      ref={zoneRef}
      className={className}
      component="div"
      sx={{
        position: 'relative',
        transition: 'all 0.2s ease-in-out',
        backgroundColor: isDragging ? 'action.hover' : 'transparent',
        borderRadius: isDragging ? 1 : 0,
        border: isDragging ? `2px dashed ${theme.palette.primary.main}` : 'none',
        ...sx,
      }}
    >
      {children}

      {/* Drag overlay with visual feedback */}
      {isDragging && (
        <Paper
          elevation={3}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(25, 118, 210, 0.08)',
            borderRadius: 1,
            backdropFilter: 'blur(0px)',
            zIndex: 10,
            pointerEvents: 'none',
            animation: 'pulse 0.5s ease-in-out infinite',
            '@keyframes pulse': {
              '0%': {
                opacity: 0.8,
              },
              '50%': {
                opacity: 0.4,
              },
              '100%': {
                opacity: 0.8,
              },
            },
          }}
        >
          <Stack alignItems="center" spacing={2}>
            <CloudUpload
              sx={{
                fontSize: 48,
                color: 'primary.main',
              }}
            />
            <Typography
              variant="h6"
              sx={{
                color: 'primary.main',
                fontWeight: 600,
              }}
            >
              {t('dragDrop.dropFilesHere')}
            </Typography>
          </Stack>
        </Paper>
      )}
    </Box>
  );
}

export type { UseDragDropOptions };
// eslint-disable-next-line react-refresh/only-export-components
export { useDragDrop };
